import { useCallback, useEffect, useState } from 'react'
import { syncService } from '@core/services/sync/SyncService'
import type { QueuedSale } from '@core/db/models'
import { btn, btnBase, btnSize, cx, hint, table, tableWrap, td, th } from '@ui/classes'
import { errBanner } from '@ui/admin'
import { shortId } from '@core/utils/deviceLabel'
import { money } from '@core/utils/format'

/**
 * «Очередь синхронизации»: чеки, которые облако отклонило (422), с причиной
 * и кнопкой повторной отправки (ARCH-TASK-65.9.1.1).
 *
 * Отклонённые чеки не ретраятся автоматически (иначе блокируют всю очередь),
 * поэтому человек должен увидеть их и решить, когда отправить снова.
 */
export function SyncQueueSection({ refreshKey = 0 }: { refreshKey?: number }) {
  const [items, setItems] = useState<QueuedSale[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      setErr(null)
      setItems(await syncService.listRejectedSales(50))
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload, refreshKey])

  const retry = async (id: string) => {
    setBusy(true)
    try {
      await syncService.retryRejectedSale(id)
      await reload()
      // Пробуем отправить сразу, не дожидаясь периодического цикла.
      void syncService.syncAll().catch(() => undefined)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  // Нет проблем — не занимаем место в разделе.
  if (items.length === 0 && !err) return null

  return (
    <div>
      <p className={hint}>
        Облако отклонило эти чеки: они остались на кассе, но не ушли в облако и не
        отправляются повторно автоматически. Устраните причину и нажмите «Повторить».
      </p>
      {err && <p className={errBanner}>{err}</p>}
      <div className={tableWrap}>
        <table className={table}>
          <thead>
            <tr>
              <th className={th}>Чек</th>
              <th className={th}>Дата</th>
              <th className={th}>Сумма</th>
              <th className={th}>Причина</th>
              <th className={th}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {items.map((sale) => (
              <tr key={sale.id}>
                <td className={cx(td, 'whitespace-nowrap font-mono text-tiny')}>
                  {shortId(sale.id)}
                </td>
                <td className={cx(td, 'whitespace-nowrap')}>
                  {new Date(sale.sold_at).toLocaleString('ru-RU')}
                </td>
                <td className={cx(td, 'whitespace-nowrap tabular-nums')}>
                  {money(Number(sale.total_amount))}
                </td>
                <td className={cx(td, 'text-small text-dangerDark')}>
                  {sale.sync_error ?? '—'}
                </td>
                <td className={td}>
                  <button
                    className={cx(btnBase, btn.secondary, btnSize.sm)}
                    disabled={busy}
                    onClick={() => void retry(sale.id)}
                  >
                    Повторить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
